<template>
  <div v-if="accordionList.length" class="accordion">
    <div v-for="group in accordionList" :key="group.id" class="accordion-group">
      <p v-if="group.title" :id="group.id" class="title">
        {{ group.title }}
      </p>
      <ul v-if="group.items.length" class="accordion-groups">
        <li v-for="item in group.items" :key="item.id" :class="{ 'is-open': item.isOpen }" class="accordion-item">
          <div class="accordion-item-title" v-on:click="toggle(item, $event)">
            <a :id="item.id">{{ item.title }}</a>
            <PlusIcon :selected="item.isOpen" />
          </div>

          <div class="body">
            <div class="body-container" v-html="item.body"></div>
            <div v-if="item.images" class="body-container">
              <div v-for="image in item.images" :key="image.id">
                <BackstageImage v-if="image.url" :src="image.url" class="faq-image" :alt="image.description" />
              </div>
            </div>
            <div v-if="item.video" class="body-container">
              <BackstageVideo :src="item.video.url" class="faq-video" :poster="item.video.thumbnail" />
            </div>
          </div>
        </li>
      </ul>
    </div>
  </div>
</template>

<script lang="ts">
import BackstageVideo from '@/components/common/BackstageVideo.vue';
import { utilsMixins } from '@/mixins/utils.mixins';
import { AccordionGroup } from '@/models/accordion-group.model';
import { AccordionItem } from '@/models/accordion-item.model';
import { defineComponent, type PropType } from 'vue';
import BackstageImage from './BackstageImage.vue';
import PlusIcon from './plus-icon.vue';

export default defineComponent({
  name: 'accordion-list',
  props: {
    list: {
      required: true,
      type: Array as PropType<AccordionGroup[]>,
      default: new Array<AccordionGroup>(),
    },
    selectedGroupId: {
      required: false,
      type: String,
      default: '',
    },
  },
  data() {
    return {
      accordionList: this.list,
    };
  },
  watch: {
    list() {
      this.accordionList = this.list;
    },
  },
  updated() {
    utilsMixins.scrollToId(this.selectedGroupId);
  },
  methods: {
    toggle(item: AccordionItem, event: Event) {
      const el = event.target as HTMLElement;
      let itemBodyEl = null;
      if (!el?.parentElement) {
        return;
      }
      for (let element = el.parentElement; ; element = element.parentElement) {
        itemBodyEl = element.querySelector('.body') as HTMLElement;
        if (itemBodyEl) {
          break;
        }
        if (!element.parentElement) {
          break;
        }
      }
      item.isOpen = !item.isOpen;
      itemBodyEl.style.height = item.isOpen ? itemBodyEl.scrollHeight + 'px' : '0';
    },
  },
  components: { PlusIcon, BackstageVideo, BackstageImage },
});
</script>
<style scoped lang="scss">
.accordion {
  .accordion-groups {
    list-style: none;
    margin: 0;
    padding: 0;
  }

  .accordion-group {
    font-size: 24px;
    text-align: left;
    margin-top: 18px;

    .title {
      font-size: 20px;
      padding: 16px 0;
      margin: 0;
    }

    ~ .accordion-group {
      margin-top: 40px;
    }

    .accordion-item {
      background-color: var(--primary-bg-color);
      color: var(--subtle-color);
      margin-bottom: 1px;
      overflow-x: hidden;
      position: relative;

      .accordion-item-title {
        display: flex;
        flex-direction: row;
        justify-content: space-between;
        padding: 25px 20px;
        font-size: 16px;
        font-weight: bold;
        align-items: center;
        cursor: pointer;
      }

      &.is-open {
        .body {
          transition-timing-function: cubic-bezier(0.175, 0.885, 0.32, 1.275);
        }
      }

      .body {
        overflow: hidden;
        transition-property: height;
        transition-duration: 0.4s;
        height: 0;
        transition-timing-function: cubic-bezier(0.6, -0.28, 0.735, 0.045);
        white-space: pre-line;
        font-size: 14px;
        line-height: 150%;

        .body-container:deep() {
          padding: 0 20px 32px 20px;
          max-width: 768px;

          p {
            margin: 0;
            color: var(--secondary-color);
          }
          .faq-image {
            display: flex;
            flex-direction: column;
            column-gap: 2em;
            padding-bottom: 2em;
            max-width: 100%;
            height: auto;
          }
          .faq-video {
            display: flex;
            flex-direction: column;
            column-gap: 2em;
            padding-bottom: 2em;
            max-width: 100%;
            height: auto;
          }
        }
      }
    }
  }
}
</style>
